import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  FileText,
  Download,
  Plus,
  Calculator,
  CheckCircle2,
  Clock,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Banknote,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "wouter";
import { EmptyState } from "@/components/ui/empty-state";

export default function TaxReports() {
  return (
    <DashboardLayout>
      <TaxReportsContent />
    </DashboardLayout>
  );
}

function TaxReportsContent() {
  const { selectedCompanyId } = useCompany();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [typeFilter, setTypeFilter] = useState<string>("all");

  // Fetch tax reports
  const { data: taxReports, isLoading: reportsLoading } =
    trpc.taxExport.listTaxReports.useQuery(
      {
        companyId: selectedCompanyId || 0,
        type: typeFilter !== "all" ? (typeFilter as any) : undefined,
      },
      { enabled: !!selectedCompanyId }
    );

  // Fetch export stats for summary cards
  const { data: stats } = trpc.taxExport.getExportStats.useQuery(
    {
      companyId: selectedCompanyId || 0,
      taxYear: selectedYear,
    },
    { enabled: !!selectedCompanyId }
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("de-CH", {
      style: "currency",
      currency: "CHF",
    }).format(amount / 100);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("de-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "draft":
        return (
          <Badge variant="outline" className="text-muted-foreground">
            <Clock className="h-3 w-3 mr-1" />
            Draft
          </Badge>
        );
      case "calculated":
        return (
          <Badge variant="secondary" className="text-[var(--info)]">
            <Calculator className="h-3 w-3 mr-1" />
            Calculated
          </Badge>
        );
      case "filed":
        return (
          <Badge className="bg-[var(--success)]/10 text-[var(--success)] hover:bg-[var(--success)]/20">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Filed
          </Badge>
        );
      case "paid":
        return (
          <Badge className="bg-primary/10 text-primary hover:bg-primary/20">
            <Banknote className="h-3 w-3 mr-1" />
            Paid
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      vat: "VAT / MWST",
      corporate_tax: "Corporate Tax",
      withholding_tax: "Withholding Tax",
      stamp_duty: "Stamp Duty",
    };
    return labels[type] || type;
  };

  if (!selectedCompanyId) {
    return (
      <div className="container py-8">
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">No Company Selected</h2>
          <p className="text-muted-foreground mb-4">
            Please select a company to view tax reports.
          </p>
          <Link href="/companies">
            <Button>Go to Companies</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8 space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tax Reports</h1>
          <p className="text-muted-foreground mt-1">
            Manage and track your Swiss tax filings
          </p>
        </div>
        <div className="flex gap-2">
          <Link href="/tax-export">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Generate New Export
            </Button>
          </Link>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Net Profit</CardTitle>
            <TrendingUp className="h-4 w-4 text-[var(--success)]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? formatCurrency(stats.netProfit) : "—"}
            </div>
            <p className="text-xs text-muted-foreground">
              Tax year {selectedYear}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">VAT Payable</CardTitle>
            <Calculator className="h-4 w-4 text-[var(--info)]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? formatCurrency(stats.vatPayable) : "—"}
            </div>
            <p className="text-xs text-muted-foreground">Net VAT due</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Deductions
            </CardTitle>
            <TrendingDown className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? formatCurrency(stats.totalDeductionAmount) : "—"}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats?.deductionCount || 0} deductions
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Transactions</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.transactionCount || 0}
            </div>
            <p className="text-xs text-muted-foreground">Total records</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Select
          value={selectedYear.toString()}
          onValueChange={v => setSelectedYear(parseInt(v))}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select year" />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: 5 }, (_, i) => {
              const y = new Date().getFullYear() - i;
              return (
                <SelectItem key={y} value={y.toString()}>
                  {y === new Date().getFullYear() ? `${y} (YTD)` : y}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="vat">VAT / MWST</SelectItem>
            <SelectItem value="corporate_tax">Corporate Tax</SelectItem>
            <SelectItem value="withholding_tax">Withholding Tax</SelectItem>
            <SelectItem value="stamp_duty">Stamp Duty</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tax Reports Table */}
      <Card>
        <CardHeader>
          <CardTitle>Filed Reports</CardTitle>
          <CardDescription>
            Your submitted and pending tax reports
          </CardDescription>
        </CardHeader>
        <CardContent>
          {reportsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : taxReports && taxReports.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tax Amount</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {taxReports.map(report => (
                  <TableRow key={report.id}>
                    <TableCell className="font-medium">
                      {getTypeLabel(report.type)}
                    </TableCell>
                    <TableCell>{report.period}</TableCell>
                    <TableCell>{formatDate(report.dueDate)}</TableCell>
                    <TableCell>{getStatusBadge(report.status)}</TableCell>
                    <TableCell>
                      {report.data?.taxAmount
                        ? formatCurrency(report.data.taxAmount)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          toast.info("Download feature available in Tax Export")
                        }
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <EmptyState
              icon={FileText}
              title="No Tax Reports Found"
              description="You haven't created any tax reports yet. Generate your first export to get started."
              actions={[
                {
                  label: "Generate Tax Export",
                  onClick: () => {
                    window.location.href = "/tax-export";
                  },
                },
              ]}
            />
          )}
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card
          className="hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => (window.location.href = "/tax-export")}
        >
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Export Tax Data
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Generate XML, PDF, or CSV exports for Swiss tax authorities
            </p>
          </CardContent>
        </Card>

        <Card
          className="hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => (window.location.href = "/deduction-optimizer")}
        >
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calculator className="h-5 w-5 text-[var(--success)]" />
              Optimize Deductions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              AI-powered suggestions to maximize your tax deductions
            </p>
          </CardContent>
        </Card>

        <Card
          className="hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => (window.location.href = "/rnd-projects")}
        >
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-[var(--info)]" />
              R&D Tax Credits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Track R&D projects for additional tax benefits
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
